import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { CommentsService } from '@watchlist/shared';
import { font } from '../../utils/fontHelper';
import { useToast } from '../utils/ToastProvider';
import { setUserBlocked } from '../../services/functionsApi';
import { formatFirebaseDate } from '../../utils/dateUtils';

/** Aggregated row: one entry per unique reported commentId, with the list of reporters. */
interface ReportedCommentRow {
  commentId: string;
  commentAuthorUid: string | null;
  commentAuthorName: string | null;
  commentText: string;
  parentType: string | null;
  parentId: string | null;
  reporterUids: string[];
  /** ISO of the most recent report against this comment. */
  lastReportedAt: string;
}

const parentTypeLabel = (t: string | null): string => {
  switch (t) {
    case 'review':    return 'Review';
    case 'crewPost':  return 'Crew post';
    case 'watchlist': return 'Watchlist';
    case 'bundle':    return 'Activity bundle';
    default:          return 'Unknown';
  }
};

const ReportedCommentsPanel: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { showSuccess, showError } = useToast();

  const [rows, setRows] = useState<ReportedCommentRow[]>([]);
  const [loading, setLoading] = useState(true);
  /** commentId currently being acted on — disables that row's buttons. */
  const [busyCommentId, setBusyCommentId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const reports = await CommentsService.listReportedComments(200);

      // Group reports by commentId. Each row aggregates reporter UIDs and tracks the
      // most-recent reportedAt for sort order.
      const byComment = new Map<string, ReportedCommentRow>();
      for (const r of reports) {
        const existing = byComment.get(r.commentId);
        if (existing) {
          if (!existing.reporterUids.includes(r.reporterUid)) {
            existing.reporterUids.push(r.reporterUid);
          }
          if (r.reportedAt > existing.lastReportedAt) {
            existing.lastReportedAt = r.reportedAt;
          }
        } else {
          byComment.set(r.commentId, {
            commentId: r.commentId,
            commentAuthorUid: r.commentAuthorUid,
            commentAuthorName: r.commentAuthorName,
            commentText: r.commentText,
            parentType: r.parentType,
            parentId: r.parentId,
            reporterUids: [r.reporterUid],
            lastReportedAt: r.reportedAt,
          });
        }
      }

      const list = Array.from(byComment.values()).sort(
        (a, b) => (a.lastReportedAt < b.lastReportedAt ? 1 : -1)
      );
      setRows(list);
    } catch (e) {
      console.error('ReportedCommentsPanel: load failed', e);
      showError('Could not load reported comments.');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleDeleteComment = async (row: ReportedCommentRow) => {
    setBusyCommentId(row.commentId);
    try {
      await CommentsService.deleteComment(row.commentId);
      await CommentsService.dismissReportsForComment(row.commentId);
      showSuccess('Comment deleted and reports cleared.');
      setRows(prev => prev.filter(r => r.commentId !== row.commentId));
    } catch (e) {
      console.error('ReportedCommentsPanel: delete failed', e);
      showError('Could not delete the comment.');
    } finally {
      setBusyCommentId(null);
    }
  };

  const handleBlockAuthor = async (row: ReportedCommentRow) => {
    if (!row.commentAuthorUid) return;
    setBusyCommentId(row.commentId);
    try {
      await setUserBlocked(row.commentAuthorUid, true);
      showSuccess(`Blocked ${row.commentAuthorName || 'user'}.`);
    } catch (e) {
      console.error('ReportedCommentsPanel: block failed', e);
      showError('Could not block the user.');
    } finally {
      setBusyCommentId(null);
    }
  };

  const handleDismiss = async (row: ReportedCommentRow) => {
    setBusyCommentId(row.commentId);
    try {
      await CommentsService.dismissReportsForComment(row.commentId);
      showSuccess('Reports dismissed.');
      setRows(prev => prev.filter(r => r.commentId !== row.commentId));
    } catch (e) {
      console.error('ReportedCommentsPanel: dismiss failed', e);
      showError('Could not dismiss reports.');
    } finally {
      setBusyCommentId(null);
    }
  };

  const totalCount = useMemo(() => rows.length, [rows]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (totalCount === 0) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography sx={{ fontFamily: font(), color: theme.palette.text.secondary }}>
          No reported comments. The queue is clear.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography sx={{ fontFamily: font(), color: theme.palette.text.secondary, fontSize: 14, mb: 2 }}>
        {totalCount} comment{totalCount === 1 ? '' : 's'} flagged by users. Take action or dismiss the report.
      </Typography>

      <Stack divider={<Divider />} spacing={0}>
        {rows.map(row => {
          const busy = busyCommentId === row.commentId;
          return (
            <Box key={row.commentId} sx={{ py: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Chip
                  label={parentTypeLabel(row.parentType)}
                  size="small"
                  sx={{ fontFamily: font(), fontSize: 11, fontWeight: 600 }}
                />
                <Chip
                  label={`${row.reporterUids.length} report${row.reporterUids.length === 1 ? '' : 's'}`}
                  size="small"
                  color="warning"
                  sx={{ fontFamily: font(), fontSize: 11, fontWeight: 600 }}
                />
                <Typography sx={{ fontFamily: font(), fontSize: 12, color: theme.palette.text.secondary }}>
                  {formatFirebaseDate(row.lastReportedAt, '', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                </Typography>
              </Stack>

              <Typography sx={{
                fontFamily: font(),
                fontWeight: 600,
                fontSize: 14,
                color: theme.palette.text.primary,
                mb: 0.25,
              }}>
                {row.commentAuthorName || 'Unknown user'}
              </Typography>

              <Box
                sx={{
                  p: 1.5,
                  mb: 1,
                  borderRadius: 1.5,
                  bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                }}
              >
                <Typography sx={{
                  fontFamily: font(),
                  fontSize: 14,
                  color: theme.palette.text.primary,
                  whiteSpace: 'pre-line',
                  wordBreak: 'break-word',
                }}>
                  {row.commentText || <em>(empty)</em>}
                </Typography>
              </Box>

              <Typography sx={{
                fontFamily: font(),
                fontSize: 12,
                color: theme.palette.text.secondary,
                mb: 1.5,
              }}>
                On {parentTypeLabel(row.parentType).toLowerCase()} <code>{row.parentId || '—'}</code>
              </Typography>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  onClick={() => handleDeleteComment(row)}
                  disabled={busy}
                  sx={{ fontFamily: font(), textTransform: 'none' }}
                >
                  Delete comment
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  onClick={() => handleBlockAuthor(row)}
                  disabled={busy || !row.commentAuthorUid}
                  sx={{ fontFamily: font(), textTransform: 'none' }}
                >
                  Block author
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => handleDismiss(row)}
                  disabled={busy}
                  sx={{ fontFamily: font(), textTransform: 'none' }}
                >
                  Dismiss reports
                </Button>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default ReportedCommentsPanel;
